'use client';

import { useState, useEffect } from 'react';

import {
  Autocomplete as AutocompleteMUI,
  TextField,
  CircularProgress,
  createFilterOptions,
} from '@mui/material';

import { autocompleteHandleChange } from './handlers/handleChange/handleChange.util';
import { setInitialValue } from './utils/localValue/localValue.util';

import type { TextFieldProps } from '@mui/material';
import type { TNameType } from '../../types/components/components.type';
import type {
  IAutocompleteBaseProps,
  TAutocompleteBaseValue,
  TAvailableValueTypeFreeSolo,
  TLocalHandleChange,
} from './autocompleteBase.type';

export const AutocompleteBase = <TName extends TNameType>({
  name,
  value,
  availableValues = [],
  handleChange,
  handleInputChange,
  handleBlur,
  label,
  errorMessage = '',
  helperText,
  className = '',
  size = 'medium',
  color = 'primary',
  disabled = false,
  multiple = false,
  loading = false,
  freeSolo = false,
  sx = {},
  getOptionLabel,
  filterOptions,
}: IAutocompleteBaseProps<TName>) => {
  const defaultLocalValue: TAutocompleteBaseValue = !multiple ? null : [];
  const [localValue, setLocalValue] = useState<TAutocompleteBaseValue>(defaultLocalValue);
  const clearLocalValue = () => setLocalValue(defaultLocalValue);

  const localHandleChange: TLocalHandleChange = (newValue, localAvailableValues, reason) => {
    autocompleteHandleChange(
      {
        name,
        value,
        newValue,
        reason,
        availableValues: localAvailableValues,
      },
      { clearLocalValue, handleChange, setLocalValue },
    );
  };

  useEffect(() => {
    setInitialValue({
      handleChange: localHandleChange,
      value,
      localValue,
      multiple,
      availableValues,
      defaultLocalValue,
    });
  }, [availableValues.length, multiple, value]); // look only on outside values and options change

  const filter = createFilterOptions<TAvailableValueTypeFreeSolo>();

  const isError = !!errorMessage;

  return (
    <AutocompleteMUI
      fullWidth
      noOptionsText="Brak"
      loadingText="Wczytuję ..."
      className={className}
      options={availableValues}
      onBlur={handleBlur}
      disabled={disabled}
      disableCloseOnSelect={multiple}
      // Assertion because MUI makes from value own types for single and multiple value
      value={localValue as TAvailableValueTypeFreeSolo}
      multiple={multiple}
      loading={loading}
      size={size}
      freeSolo={freeSolo}
      onChange={(_e, currentValue, reason) => {
        if (typeof currentValue === 'string') {
          localHandleChange({ value: currentValue, label: currentValue }, availableValues, reason);
        } else {
          // Assertion because MUI have mishmash in types
          localHandleChange(currentValue as TAutocompleteBaseValue, availableValues, reason);
        }
      }}
      filterOptions={
        filterOptions ??
        ((options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;

          const isExisting = options.some((option) => inputValue === option.label);

          if (freeSolo && inputValue !== '' && !isExisting) {
            filtered.push({
              value: inputValue,
              label: `Dodaj: "${inputValue}"`,
              freeSolo: true,
            });
          }

          return filtered;
        })
      }
      getOptionLabel={
        getOptionLabel ??
        ((option) => {
          if (typeof option === 'string') {
            return option;
          }

          if (option.freeSolo) {
            return option.value;
          }

          return option.label;
        })
      }
      renderOption={(props, option) => {
        if (typeof option === 'string') {
          return (
            <li {...props} key={option}>
              {option}
            </li>
          );
        }

        const { value: avValue, label: avLabel } = option;
        return (
          <li {...props} key={avValue}>
            {avLabel}
          </li>
        );
      }}
      onInputChange={(_, inputValue, inputReason) =>
        handleInputChange && handleInputChange(inputValue, inputReason)
      }
      isOptionEqualToValue={(option, { value: avValue }) => option.value === avValue}
      renderInput={({ InputProps, ...renderInputProps }) => (
        <TextField
          {...(renderInputProps as TextFieldProps)}
          variant="outlined"
          label={label}
          name={name}
          error={isError}
          helperText={isError ? errorMessage : helperText}
          color={color}
          slotProps={{
            input: {
              ...InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      sx={sx}
    />
  );
};
